import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Linking, TouchableOpacity } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { Text } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useUpdateUserInformation } from '@/feature/kyc/hooks/use-update-user-information';
import { useUserAcceptProductTerms } from '@/feature/terms-and-conditions/hooks/use-user-accept-product-terms';
import { LOG_TAG } from '@/feature/terms-and-conditions/utils/terms-and-conditions-utils';
import { minGamblingAgeSelector, useAuthUserConfig } from '@/hooks/use-auth-user-config';
import { useAvailableProducts } from '@/hooks/use-available-products';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { usePrefetchPromoCodeDefaultProductSyncMap } from '@/hooks/use-promo-code-default-product-sync';
import { KycStackScreenProps } from '@/navigation/kyc/types';
import { ProductDocumentDataBodyScreenLinksSliceItem } from '@/types/prismic.generated';
import { logger } from '@/utils/logging';

import { AcknowledgementRow } from '../../components/Acknowledgements/AcknowledgementsRow';
import { KycStepContainer } from '../../components/KycStepContainer';
import { KycProvider, kycRouteNames } from '../../const';
import { convertDobToObject } from '../../formatter';
import { initialAcknowledgementValues, useKycFieldsStore } from '../../hooks/use-kyc-fields';

type ScreenProps = KycStackScreenProps<'KycAcknowledgements' | 'KycAcknowledgementsRetry'>;

const EMPTY: ProductDocumentDataBodyScreenLinksSliceItem[] = [];

export const TransComponent = () => {
    const navigation = useNavigation();
    const links = useJurisdictionStore(state => state.jurisdictionSettings?.productConfig?.screenLinks) ?? EMPTY;
    const tnc = links.find(link => link.link_type === 'TermsAndConditions')?.url || '';
    const privacyPolicy = links.find(link => link.link_type === 'PrivacyPolicy')?.url || '';

    const handleOnPress = (uri: string) => {
        navigation.navigate('ModalWebView', { uri });
    };

    return (
        <Trans
            i18nKey="ack_privacy_policy"
            ns="kyc"
            components={{
                terms: (
                    <TouchableOpacity onPress={() => handleOnPress(tnc)}>
                        <Text lineHeight={16} textDecorationLine="underline" />
                    </TouchableOpacity>
                ),
                privacy: (
                    <TouchableOpacity onPress={() => handleOnPress(privacyPolicy)}>
                        <Text lineHeight={16} textDecorationLine="underline" />
                    </TouchableOpacity>
                ),
            }}
        />
    );
};

export const AcknowledgementsStep = ({ navigation, route }: ScreenProps) => {
    const setAcknowledgementFieldValues = useKycFieldsStore(state => state.setAcknowledgementValues);
    const kycValues = useKycFieldsStore(state => state.values);
    const { t } = useTranslation('kyc');
    const [updateUserInfoError, setErrorMessage] = useState<string>();
    const { data: minGamblingAge } = useAuthUserConfig({ select: minGamblingAgeSelector, staleTime: 0 });
    const [acknowledgementValues, setAcknowledgementValues] = useState(initialAcknowledgementValues);
    const hasSocialWallet = useHasSocialWallet();
    const { availableProducts } = useAvailableProducts();
    const hasMultipleProducts = availableProducts.filter(product => product !== Product.None).length > 1;

    usePrefetchPromoCodeDefaultProductSyncMap();

    const { mutateAsync: updateUserInfo, isPending } = useUpdateUserInformation();
    const provider = route.params?.provider;

    const acceptProductTermsMutation = useUserAcceptProductTerms();

    const acknowledgements = [
        {
            key: 'tncAcknowledgementAccepted',
            text: <TransComponent />,
            hasLink: true,
        },
        {
            key: 'infoAcknowledgementAccepted',
            text: t('ack_tax_purposes'),
            hasLink: false,
        },
        {
            key: 'prohibitedAcknowledgementAccepted',
            text: t('ack_prohibited_participant'),
            hasLink: false,
        },
        {
            key: 'ageAcknowledgementAccepted',
            text: t('ack_age', { age: minGamblingAge ?? 18 }),
            hasLink: false,
        },
    ] as const;

    const isNextDisabled = !acknowledgements.every(({ key }) => acknowledgementValues[key]) || isPending;

    const handleOnPress = async () => {
        BetrAnalytics.trackEvent(AnalyticsEvent.TCS_SUBMIT);
        setAcknowledgementFieldValues(acknowledgementValues);
        if (provider === KycProvider.TRUSTLY) {
            BetrAnalytics.trackEvent(AnalyticsEvent.KYC_TRUSTLY_TCS_SUCCESS);
            if (hasMultipleProducts) {
                navigation.navigate('DefaultProductOnboarding', { kycProvider: KycProvider.TRUSTLY });
            } else if (hasSocialWallet) {
                navigation.navigate('DepositStack', {
                    screen: 'CoinPackageScreen',
                    params: { isTrustlyKyc: true },
                });
            } else {
                navigation.navigate('TrustlyNumpad');
            }
            return;
        }

        try {
            await acceptProductTermsMutation.mutateAsync();
        } catch (error) {
            logger.error(LOG_TAG, 'Error accepting user terms and conditions', error);
        }

        navigation.push(kycRouteNames.VERIFY_IDENTITY);
    };

    const handleClosePress = () => {
        BetrAnalytics.trackEvent(AnalyticsEvent.TCS_ABORT);
        // We do not want to isolate Trustly event here, we still need to track TCS_ABORT
        if (provider === KycProvider.TRUSTLY) {
            BetrAnalytics.trackEvent(AnalyticsEvent.KYC_TRUSTLY_TCS_ABORT);
        }
        navigation.goBack();
    };

    const onSubmit = async () => {
        setErrorMessage(undefined);
        if (provider === KycProvider.TRUSTLY) {
            return true;
        }
        try {
            await updateUserInfo({
                address: {
                    first_name: kycValues.firstName.trim(),
                    last_name: kycValues.lastName.trim(),
                    street_name: kycValues.address.trim(),
                    city: kycValues.city.trim(),
                    state: kycValues.americanState.trim(),
                    post_code: kycValues.zip.trim(),
                    country: 'US',
                    national_id: kycValues.ssn.trim(),
                    date_of_birth: convertDobToObject(kycValues.dateOfBirth),
                },
            });
        } catch (error: unknown) {
            if (error instanceof Error) {
                setErrorMessage(error.message);
                throw error;
            }
        }
        return true;
    };

    return (
        <KycStepContainer
            title={t('acknowledgements_header')}
            subText={t('acknowledgements_copy')}
            buttonText={provider === 'trustly' ? 'kyc:verify_identity_and_deposit' : 'kyc:verify_identity'}
            onPressButton={handleOnPress}
            {...{ isNextDisabled }}
            onFormSubmit={onSubmit}
            onCloseIconPress={handleClosePress}
            errorMessage={updateUserInfoError}
        >
            {acknowledgements.map(({ key, text, hasLink }) => (
                <AcknowledgementRow
                    key={key}
                    isRowClickable={!hasLink}
                    checkBoxValue={acknowledgementValues[key]}
                    setCheckBoxValue={() =>
                        setAcknowledgementValues(prevState => ({ ...prevState, [key]: !acknowledgementValues[key] }))
                    }
                >
                    {text}
                </AcknowledgementRow>
            ))}
            <Text pt="s24" variant="bodySmall" color={ds.colorToken.text.secondary}>
                {t('ack_rg_copy1')}
                {'\n'}
            </Text>
            <Text variant="bodySmall" color={ds.colorToken.text.secondary}>
                <Trans i18nKey="ack_rg_copy2" ns="kyc">
                    If you or someone you know has a gaming problem, please call the NCPG at
                    <TouchableOpacity
                        onPress={() => {
                            Linking.openURL('tel:1-800-522-4700');
                        }}
                    >
                        <Text variant="titleSmall" lineHeight={12} textDecorationLine="underline">
                            1-800-522-4700
                        </Text>
                    </TouchableOpacity>
                </Trans>
            </Text>
        </KycStepContainer>
    );
};
